import React, { useRef, useState } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select } from 'antd';
import {
    PlusCircleOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo from '../../../constants/functions';
import EditableFormBlock from '../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import Timeline from '../Timeline';
import Attachments from '../Attachments';

const { Title, Text, Link } = Typography;

const CallLogsDetails: React.FC<any> = ({ callLog }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();

    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    const callOwnerConfig: MasterConfigs = {
        api: () => '/call-logs',
        getFormColumns: () => [
            {
                label: 'Call Status',
                name: 'callStatus',
                type: 'select',
                children: [
                    { name: 'Completed', value: 'Completed' },
                    { name: 'No Answer', value: 'No Answer' },
                    { name: 'Busy', value: 'Busy' },
                    { name: 'Failed', value: 'Failed' },
                    { name: 'Missed', value: 'Missed' }
                ],
                span: 24,
            },
            {
                label: "Call Date",
                name: "callDate",
                type: "date",
                render: (text) => text ? moment(text).format('DD MMMM YYYY HH:mm') : 'N/A',
                rules: [{ required: true, message: 'Call Date is required' }],
                span: 24,
            },
            {
                label: "Call Type",
                name: "callType",
                type: "select",
                children: [
                    { name: 'Inbound', value: 'Inbound' },
                    { name: 'Outbound', value: 'Outbound' }
                ],
                span: 24,
            },
            {
                label: 'Call Owner',
                name: 'callOwner',
                type: 'select',
                isCombo: true,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A';
                },
                commonProps: {
                    disabled: true,
                },
                span: 24,
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'callOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            }
        ]
    }

    const callInfoConfig: MasterConfigs = {
        api: () => '/call-logs',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Call Information</Title>
            },
            {
                label: 'Call Owner',
                name: 'callOwner',
                type: 'select',
                isCombo: true,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A';
                },
                span: 24,
            },
            {
                label: "Subject", name: "subject", type: "text", span: 24,
                rules: [{ required: true, message: 'Subject is required' }]
            },
            {
                label: "Phone Number",
                name: "phoneNumber",
                type: "text",
                span: 24,
                rules: [{ required: true, message: 'Phone Number is required' }]
            },
            {
                label: "Call Type",
                name: "callType",
                type: "select",
                children: [
                    { name: 'Inbound', value: 'Inbound' },
                    { name: 'Outbound', value: 'Outbound' }
                ],
                span: 24,
            },
            {
                label: "Call Date",
                name: "callDate",
                type: "date",
                render: (text) => text ? moment(text).format('DD MMMM YYYY HH:mm') : 'N/A',
                rules: [{ required: true, message: 'Call Date is required' }],
                span: 24,
            },
            {
                label: "Call Status",
                name: "callStatus",
                type: "select",
                children: [
                    { name: 'Completed', value: 'Completed' },
                    { name: 'No Answer', value: 'No Answer' },
                    { name: 'Busy', value: 'Busy' },
                    { name: 'Failed', value: 'Failed' },
                    { name: 'Missed', value: 'Missed' }
                ],
                span: 24,
            },
            {
                label: "Call Duration (seconds)",
                name: "callDuration",
                type: "number",
                span: 24,
            },
            {
                label: 'Call For', name: 'refType', type: 'select',
                commonProps: {
                    disabled: true,
                },
                span: 24,
            },
            {
                label: "Call For", name: "refId", type: "select",
                isCombo: true,
                render: (text) => {
                    return text?.businessName || [text?.firstName, text?.lastName].filter(Boolean).join(' ') || text?.dealName || 'N/A';
                },
                commonProps: {
                    disabled: true,
                },
                span: 24
            },
            {
                label: 'Modified By',
                name: 'updatedBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {text?.updatedAt ? moment(text?.updatedAt).format('llll') : 'N/A'}
                        </Text>
                    </div>
                },
                span: 24,
            },
            {
                label: 'Created By',
                name: 'createdBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {text?.createdAt ? moment(text?.createdAt).format('llll') : 'N/A'}
                        </Text>
                    </div>
                },
                span: 24,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'callOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            callLog?.refType && {
                api: `/tasks-management/ref-types/list?refType=${callLog.refType}`,
                name: 'refId',
                idKey: '_id',
                render: (text) => text.name,
                rerender: [callLog?.refType]
            }
        ].filter(Boolean),
    }

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails' },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];


    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    if (!callLog) {
        return <div>No call log selected</div>;
    }

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(callLog?.updatedAt)}</Text>
                        </div>
                    </div>

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={callOwnerConfig}
                                        id={callLog?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={callInfoConfig}
                                        id={callLog?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        {sections.map(section => (
                            <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'CallLogs'} refId={callLog?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'CallLogs'} refId={callLog?._id} /> </div>}
                            </div>
                        ))}
                    </div>)}
                    {activeTab === 'timeline' && <Timeline type="CallLogs" id={callLog?._id} />}

                </div>
            </div>
        </div>
    );
};

export default CallLogsDetails;
